import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useWindowDimensions,
  FlatList,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Dots from 'react-native-dots-pagination';
import AutoHeightWebView from 'react-native-autoheight-webview';
import GestureRecognizer from 'react-native-swipe-gestures';
import PagerView from 'react-native-pager-view';
import {SafeAreaView, useSafeAreaInsets} from 'react-native-safe-area-context';

import Text from '../../components/elements/Text';
import Button from '../../components/elements/Button';
import CachedImage from '../../components/elements/CachedImage';
import IconButton from '../../components/elements/IconButton';
import Divider from '../../components/elements/Divider';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES, COLORS} from '../../utils/Constants';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {fetchApi} from '../../utils/ApiHelper';
import {HTTP_METHODS, PRODUCTS} from '../../utils/URLConstants';
import {shopContext} from '../../contexts/ShopContext';
import {DLog, generateAssetsFontCss} from '../../utils/HelperMethods';
import {trackEvent} from '../../utils/Analytics';
import {
  CartIcon,
  CloseIcon,
  DownArrowIcon,
  Logo,
  LongBackArrowIcon,
  ShareIcon,
  UpArrowIcon,
} from '../../utils/SvgIcons';

export const generateWebViewText = (title, width) => {
  let css = generateAssetsFontCss('Silka-Bold', 'ttf');
  css += generateAssetsFontCss('Silka-Regular', 'ttf');
  css += generateAssetsFontCss('Silka-Light', 'ttf');
  css += generateAssetsFontCss('Silka-SemiBold', 'ttf');
  css += generateAssetsFontCss('Silka-Medium', 'ttf');

  let str =
    `<html><head><meta name="viewport" content="initial-scale=1.0, maximum-scale=1.0"><style type="text/css"> html *
    {
       font-family: Silka-Regular
    }` +
    css +
    `</style></head><body style="margin: 0;padding: 0;">${title}</body></html>`;
  return str;
};

export const createMarkup = description => {
  return {__html: description};
};

const ViewProduct = ({
  navigation,
  route,
  setProductBeingViewed,
  setCartBeingViewed,
}) => {
  const insets = useSafeAreaInsets();
  const {width} = useWindowDimensions();
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);

  const [visibleIndex, setVisibleIndex] = useState(0);
  const [productQty, setProductQty] = useState(1);
  const [productDetail, setProductDetail] = useState(null);
  const [showImages, setShowImages] = useState(false);

  const maxQty = useRef(1);
  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);
  const ShopC = useContext(shopContext);

  useEffect(() => {
    if (route.params == null || route.params.product == null) {
      if (Platform.OS !== 'web') navigation.goBack();
    } else {
      trackEvent('View Product Information', {
        productid: route.params.product.id,
      });
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getData is re-created each render; product fetch + analytics are intentionally keyed to route params only
  }, [route.params]);

  const getData = () => {
    lpc.setVisibleWithTimeout(true);
    const id = route.params.product.id;
    fetchApi(HTTP_METHODS.GET, PRODUCTS + `/${id}`)
      .then(res => {
        lpc.setVisibleWithTimeout(false);
        const product = res.data.data;
        // maxQty.current = product.quantityPerOrder;
        // Math.min(
        //   product.quantityPerOrder,
        //   product.quantityAvailable,
        // );
        setProductDetail(product);
      })
      .catch(err => {
        DLog(err);
        esc.showError(err);
      });
  };

  const updateProductQty = qty => {
    // if (qty > 0 || qty < maxQty.current) {
    if (qty > 0) {
      setProductQty(qty);
    }
  };

  const onPressAddToCart = () => {
    trackEvent('Add to Cart', {productid: productDetail.id});

    let cartItems = [...ShopC.cartProducts];
    let index = 0;
    let inCart = false;
    if (cartItems.length > 0) {
      cartItems.forEach((i, idx) => {
        // console.log('i.id , productDetail.id', i.id, productDetail.id);
        if (i.id == productDetail.id) {
          index = idx;
          inCart = true;
        }
      });
    }
    // console.log('index', index);

    if (inCart) {
      const p = cartItems[index];
      const product = {...p, productQty: p.productQty + productQty};
      cartItems[index] = product;
      ShopC.setCartProducts(cartItems);
    } else {
      const product = {...productDetail, productQty};
      cartItems.push(product);
      ShopC.setCartProducts(cartItems);
    }
    // Adding is a destination change, not a side effect: take the user to the
    // cart so they see the item landed (previously the screen just sat on the
    // product page with no feedback beyond the header cart dot). Web renders
    // the cart as a side panel — mirror the header cart-icon behavior.
    if (Platform.OS === 'web') {
      setCartBeingViewed(true);
    } else {
      navigation.navigate('MyCart');
    }
  };

  const onSwipeDown = gestureState => {
    setShowImages(false);
  };

  const onViewableItemsChanged = useCallback(({viewableItems, changed}) => {
    setVisibleIndex(viewableItems[0].index);
    // console.log('Visible items are', viewableItems);
    // console.log('Changed in this iteration', changed);
  }, []);

  const renderImage = ({item, index}) => {
    return (
      <CachedImage
        source={item.imageUrl}
        style={styles.productFullImage}
        resizeMode="contain"
      />
    );
  };

  const ProductImages = () => {
    return (
      <Modal
        transparent
        visible={showImages}
        animationType="slide"
        onRequestClose={() => setShowImages(false)}>
        <SafeAreaView style={styles.imageModalContainer}>
          <View style={styles.iconButtonContainer}>
            <IconButton
              onPress={() => setShowImages(false)}
              imageSize={16}
              leftImage={<CloseIcon />}
              leftIconAccessibilityLabel="Close"
              tintColor={colors.textPrimary}
            />
          </View>
          <View style={[styles.centerItCarousel, styles.fullFlex]}>
            <View style={styles.carouselView}>
              <PagerView style={{flex: 1, width}} initialPage={0}>
                {productDetail.images.map((item, index) => (
                  <View
                    key={index}
                    style={{
                      flex: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    {renderImage({item})}
                  </View>
                ))}
              </PagerView>
            </View>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  const ProductHeader = () => {
    const onShare = async () => {
      try {
        let content = {};
        let options = {};
        if (Platform.OS == 'ios') {
          content = {
            title: 'Share Article',
            url: productDetail.url,
          };
        } else {
          content = {
            title: 'Share Article',
            message: productDetail.url,
          };
          options = {
            dialogTitle: 'Share Article',
          };
        }
        const result = await Share.share(content, options);

        if (result.action === Share.sharedAction) {
          if (result.activityType) {
            // shared with activity type of result.activityType
          } else {
            // shared
          }
        } else if (result.action === Share.dismissedAction) {
          // dismissed
        }
      } catch (error) {
        alert(error.message);
      }
    };

    return (
      <View style={styles.header}>
        <View style={styles.flexDirectionRow}>
          <IconButton
            onPress={
              Platform.OS === 'web'
                ? () => setProductBeingViewed({})
                : navigation.goBack
            }
            imageSize={16}
            leftImage={<LongBackArrowIcon />}
            tintColor={colors.textPrimary}
          />
        </View>
        <View style={styles.flexDirectionRow}>
          {productDetail.url && productDetail.url.length > 0 && (
            <View style={styles.marginHorizontal16}>
              <IconButton
                leftImage={<ShareIcon />}
                tintColor={colors.textPrimary}
                onPress={onShare}
              />
            </View>
          )}
          <View style={styles.flexRow}>
            <IconButton
              leftImage={<CartIcon />}
              tintColor={colors.textPrimary}
              imageSize={20}
              onPress={() =>
                Platform.OS === 'web'
                  ? setCartBeingViewed(true)
                  : navigation.navigate('MyCart')
              }
            />
            {ShopC.cartProducts.length > 0 && (
              <View style={styles.cartStatusDot} />
            )}
          </View>
        </View>
      </View>
    );
  };

  if (!productDetail) {
    return <View />;
  }

  const config = {
    velocityThreshold: 0.3,
    directionalOffsetThreshold: 80,
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <SafeAreaView>
          <ProductHeader />
        </SafeAreaView>
      </View>
      <View style={styles.imageContainer(width)}>
        {productDetail.images &&
        productDetail.images.length > 0 &&
        productDetail.images[0].imageUrl ? (
          <FlatList
            data={productDetail.images}
            horizontal
            pagingEnabled={Platform.OS !== 'web'}
            showsHorizontalScrollIndicator={false}
            renderItem={({item, index}) => (
              <Pressable
                style={styles.imageContainer(width)}
                onPress={() => setShowImages(true)}>
                <CachedImage
                  source={item.imageUrl}
                  style={styles.productImage}
                />
                {/* Image-anchored scrim: keeps header icons readable over the
                    photo, which does not change with the theme — stays literal
                    (decorative exemption, DARK-MODE.md). */}
                <LinearGradient
                  colors={[COLORS.black_70, COLORS.transparent]}
                  style={styles.gradiantView}
                />
              </Pressable>
            )}
            onViewableItemsChanged={onViewableItemsChanged}
            viewabilityConfig={{
              itemVisiblePercentThreshold: 50,
            }}
          />
        ) : (
          //add red backgroud to the image
          <View
            style={[styles.centerIt, Platform.OS === 'web' && styles.fullFlex]}>
            <Logo
              width={Platform.OS === 'web' ? 48 : 39}
              height={Platform.OS === 'web' ? 48 : 46}
            />
          </View>
        )}
        {/* Pagination dots overlay the product photo (theme-invariant), so
            their greys stay literal (decorative exemption, DARK-MODE.md).
            Rendered only when images exist — this sits outside the
            images-exist conditional above, so a product without images used
            to crash on `images.length`. */}
        {(productDetail.images?.length || 0) > 0 && (
          <View style={styles.dotView}>
            <Dots
              length={productDetail.images?.length || 0}
              active={visibleIndex}
              paddingHorizontal={5}
              marginHorizontal={8}
              activeDotWidth={10}
              activeDotHeight={10}
              passiveDotHeight={10}
              passiveDotWidth={10}
              activeColor={COLORS.grey}
              passiveColor={COLORS.grey4}
            />
          </View>
        )}
      </View>
      <View style={styles.content}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContainer}>
          <Text variation="Headline4Bold" color={colors.textPrimary}>
            {productDetail.title}
          </Text>
          <Text variation="Subtitle1SemiBold" color={colors.link} topMargin={8}>
            $ {parseFloat(productDetail.price).toFixed(2)}
          </Text>
          <Divider topMargin={8} />
          {productDetail.description && (
            <>
              <Text
                variation="Subtitle1Bold"
                color={colors.link}
                topMargin={24}>
                Product Description
              </Text>
              {Platform.OS != 'web' ? (
                <AutoHeightWebView
                  style={styles.descriptionView}
                  // Injected CSS themes the HTML description (background +
                  // text) so it reads on a dark surface; light values match
                  // the legacy white/near-black rendering.
                  customStyle={`body { background-color: ${colors.background}; color: ${colors.textPrimary}; }`}
                  onSizeUpdated={size => {}}
                  files={[
                    {
                      href: 'cssfileaddress',
                      type: 'text/css',
                      rel: 'stylesheet',
                    },
                  ]}
                  source={{
                    baseUrl: '',
                    html: generateWebViewText(productDetail.description),
                  }}
                  scrollEnabledWithZoomedin={false}
                  viewportContent={'width=device-width, user-scalable=no'}
                  bounces={false}
                  scrollEnabled={false}
                  originWhitelist={['*']}
                  useWebKit={false}
                />
              ) : (
                <div
                  style={styles.regularFont}
                  dangerouslySetInnerHTML={createMarkup(
                    productDetail.description,
                  )}
                />
              )}
            </>
          )}
          <View style={styles.rowContainer}>
            <View style={styles.rowItem(0)}>
              <Text variation="Text1SemiBold" color={colors.textMuted}>
                Qty
              </Text>
              <View style={styles.qtyContainer}>
                <Pressable
                  hitSlop={{top: 20, left: 20, bottom: 20, right: 20}}
                  onPress={() => updateProductQty(productQty - 1)}
                  disabled={productQty == 1}
                  style={styles.arrowImgContainer}>
                  <DownArrowIcon
                    width={Platform.OS === 'web' ? 8 : 10}
                    height={Platform.OS === 'web' ? 8 : 6}
                    tintColor={
                      productQty == 1
                        ? colors.borderMuted
                        : Platform.OS === 'web'
                          ? colors.link
                          : colors.textPrimary
                    }
                  />
                </Pressable>
                <View style={styles.qtyTextContainer}>
                  <Text variation="Body1Regular" color={colors.textPrimary}>
                    {productQty}
                  </Text>
                </View>
                <Pressable
                  hitSlop={{top: 20, left: 20, bottom: 20, right: 20}}
                  onPress={() => updateProductQty(productQty + 1)}
                  // disabled={!(productQty < maxQty.current)}
                  style={styles.arrowImgContainer}>
                  <UpArrowIcon
                    width={Platform.OS === 'web' ? 8 : 10}
                    height={Platform.OS === 'web' ? 8 : 6}
                    tintColor={
                      Platform.OS === 'web' ? colors.link : colors.textPrimary
                    }
                    // !(productQty < maxQty.current)
                    //   ? styles.disableArrowImg
                    //   : styles.arrowImg
                  />
                </Pressable>
              </View>
            </View>
            <View style={styles.rowItem(16)}>
              {/* Uncomment below code whenever need to show color options */}
              {/* <Text variation="Text1SemiBold" color={COLORS.grey2}>
            Color
          </Text>
          <View style={styles.qtyContainer}>
            <View style={styles.colorTextContainer}>
              <Text variation="Body1Regular" color={COLORS.black}>
                Black
              </Text>
            </View>
            <View style={styles.arrowImgContainer}>
              <DownArrowIcon
                tintColor={
                  Platform.OS === 'web' ? COLORS.mainLargeText : COLORS.black
                }
              />
            </View>
          </View> */}
              {Platform.OS === 'web' && (
                <Button
                  title={'Add to Cart'}
                  buttonType={BUTTON_TYPES.Filled}
                  onPress={onPressAddToCart}
                  titleColor={colors.textOnAccent}
                />
              )}
            </View>
          </View>
        </ScrollView>
        {Platform.OS != 'web' && (
          <Button
            title={'Add to Cart'}
            buttonType={BUTTON_TYPES.Filled}
            onPress={onPressAddToCart}
            style={[
              {
                marginVertical: insets?.bottom > 0 ? insets.bottom : 16,
              },
              styles.mh24,
            ]}
            titleColor={colors.textOnAccent}
          />
        )}
      </View>
      {productDetail.images && productDetail.images.length > 0 && (
        <GestureRecognizer
          onSwipeDown={state => onSwipeDown(state)}
          config={config}>
          {ProductImages()}
        </GestureRecognizer>
      )}
    </View>
  );
};

export default ViewProduct;
